import { Minus, Check } from "lucide-react";
import { COMPARISON } from "@/data/content";
import { Reveal } from "@/components/motion";
import ProtocolPath from "@/components/ProtocolPath";
import { SectionHead } from "@/components/Blocks";
import { CountUp } from "@/components/Strips";

const Column = ({ data, good, testId }) => (
  <div data-testid={testId} className={`p-8 sm:p-10 h-full border ${good ? "bg-[#131B2E] border-[#C5A059]/40" : "bg-transparent border-white/10"}`}>
    <p className="font-data text-[11px] tracking-[0.25em] uppercase text-slate-400">{data.sub}</p>
    <h3 className={`font-display text-3xl sm:text-4xl mt-3 ${good ? "text-[#E2C68C]" : "text-slate-300"}`}>{data.title}</h3>
    <ul className="mt-8 space-y-5">
      {data.points.map((p) => (
        <li key={p} className="flex gap-4 text-sm sm:text-base leading-relaxed">
          <span className={`mt-1 shrink-0 w-5 h-5 grid place-items-center border ${good ? "border-[#C5A059] text-[#C5A059]" : "border-white/20 text-slate-400"}`}>
            {good ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          </span>
          <span className={good ? "text-slate-100" : "text-slate-400"}>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const Comparison = () => (
  <section data-testid="comparison-section" className="bg-[#070A12] py-24 sm:py-36">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
        <SectionHead
          eyebrow="Why standard therapy fails"
          lines={["Coping is not", "the same as", "recovering."]}
          intro="Traditional CBT and basic counselling are built to help you manage symptoms. For complex trauma, that often means living alongside the memory indefinitely. Habitreat shifts the goal from symptom management to true Post-Traumatic Growth."
        />
      </div>
      <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
        <Reveal><Column data={COMPARISON.standard} testId="comparison-standard" /></Reveal>
        <Reveal delay={0.15} className="md:translate-y-12"><Column data={COMPARISON.habitreat} good testId="comparison-habitreat" /></Reveal>
      </div>
    </div>
  </section>
);

export const Framework = () => {
  return (
    <section data-testid="framework-section" className="relative bg-[#0B1120] py-24 sm:py-36 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 items-end">
          <div className="lg:col-span-7">
            <SectionHead
              eyebrow="Clinical efficacy & framework"
              lines={["Fourteen years of data.", "One measurable outcome."]}
              intro="Backed by 14 years of clinical data demonstrating a 98.4% reduction in PTSD symptoms, measured on the DSM-5 PCL-5 clinical tracking scale."
            />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 lg:text-right">
            <div className="font-display text-[7rem] sm:text-[10rem] lg:text-[12rem] leading-[0.85] text-bronze-grad" data-testid="efficacy-metric">
              <CountUp value={98.4} decimals={1} suffix="%" />
            </div>
            <p className="font-data text-[11px] tracking-[0.25em] uppercase text-slate-400 mt-4">Reduction in PTSD symptoms · PCL-5</p>
          </Reveal>
        </div>

        <ProtocolPath />
      </div>
    </section>
  );
};
